import React from 'react';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

/**
 * Sections served by the generic translatable resources API
 * (GET translations/get_by_type/{resourceType} + POST translations/save).
 *
 * Fields are labelled by the translation key the backend supplies, not localised.
 *
 * key           redux slice and i18n key, and the url segment for a `menu` entry
 * resourceTypes one or more values of Shopify's TranslatableResourceType enum. Several
 *               types share one section, which is what SELLING_PLAN and
 *               SELLING_PLAN_GROUP do - they are one thing to a merchant. Their GIDs
 *               stay distinct, and translations/save takes a mixed batch. Only a
 *               single-type section can paginate, the cursor is per section
 * menu          id of the navigation submenu to add a page of its own to
 * page          id of an existing page to add a subsection to, instead of `menu`
 * icon          navigation icon, `menu` entries only
 * expandable    `menu` entries only. false renders every translatable key as a row of
 *               one flat list. true gives each resource its own expandable item with a
 *               progress indicator, which reads better once a resource has several
 *               fields or the list gets long enough to page through
 * isNew         marks the section with a NEW badge, drop it once the section has been
 *               out for a while. On the navigation entry for a `menu` section, on the
 *               subsection row itself for a `page` one
 * prefixFieldLabels
 *               opt in to "<resource name> – <key>" labels, for a list mixing resources
 *               the bare key cannot tell apart. Selling plans are the only one
 * richTextFields
 *               render HTML content in a rich text editor. Off by default and deliberately
 *               opt-in: Shopify also reports HTML for templates carrying Liquid, and a
 *               WYSIWYG rewrites the markup it is handed, which would eat the Liquid tags
 * recommendedLengths
 *               character count per translation key, shown as a counter that turns amber once
 *               exceeded but never blocking the save. For a limit Shopify applies to the source
 *               text while still accepting a longer translation
 * maxLengths    character count Shopify enforces on the translation as well, so the input is
 *               capped instead of only counted. Nothing in the API reports either, so both are
 *               declared here and measured per key
 * loadLast      fetch after the other sections on mount. For a section heavy enough that it
 *               would otherwise hold one of the browser's few per-host connections
 * solutionsArticle
 *               key of a help center article in config/solutionsArticles.js, rendered as the
 *               help icon in the page heading. `menu` entries only, a `page` section has no
 *               heading of its own
 */
export const TRANSLATABLE_RESOURCE_SECTIONS = [
  // The Shop page's own subsections. These used to have a bespoke get/save route each and a
  // MySQL mirror behind them; they are ordinary registry sections now. Keys match the ids in
  // the page's SHOP_GROUPS, so the grouping keeps working
  {
    key: 'seo',
    resourceTypes: ['SHOP'],
    page: 'shop',
    // measured against the dev shop: a longer meta_title is accepted, meta_description is cut at 320
    recommendedLengths: { meta_title: 70 },
    maxLengths: { meta_description: 320 },
  },
  {
    key: 'payment_gateways',
    resourceTypes: ['PAYMENT_GATEWAY'],
    page: 'shop',
    prefixFieldLabels: true,
  },
  {
    key: 'shipping_methods',
    resourceTypes: ['DELIVERY_METHOD_DEFINITION'],
    page: 'shop',
    prefixFieldLabels: true,
  },
  {
    key: 'packing_slip',
    resourceTypes: ['PACKING_SLIP_TEMPLATE'],
    page: 'shop',
  },
  {
    key: 'policies',
    resourceTypes: ['SHOP_POLICY'],
    page: 'shop',
    // a policy body is a whole legal document, a full set 150-250KB
    loadLast: true,
    // prose, so a WYSIWYG is safe here. the packing slip is HTML too but carries Liquid,
    // which a rich text editor would rewrite - it stays on the plain box, like notifications
    richTextFields: true,
  },
  {
    key: 'filters',
    resourceTypes: ['FILTER'],
    menu: 'products',
    icon: <FilterAltOutlinedIcon />,
    expandable: false,
    solutionsArticle: 'collectionFilters',
  },
  {
    key: 'cookie_banner',
    resourceTypes: ['COOKIE_BANNER'],
    page: 'shop',
  },
  {
    key: 'selling_plans',
    resourceTypes: ['SELLING_PLAN', 'SELLING_PLAN_GROUP'],
    page: 'shop',
    prefixFieldLabels: true,
  },
];

/**
 * Visible order and grouping on the Shop page. The unlabelled group also takes anything not named
 * here, so a new registry section never lands under someone else's heading. Exported because the
 * dashboard breaks the Shop bar down along the same lines
 */
export const SHOP_GROUPS = [
  { id: 'general', label: null, ids: ['seo', 'policies', 'cookie_banner'] },
  { id: 'checkout', label: 'checkout_details', ids: ['payment_gateways', 'shipping_methods', 'packing_slip', 'selling_plans'] },
];

// the groups with their sections resolved, the unnamed ones appended to the unlabelled group
export const getShopGroupsWithSections = () => {
  const sections = getPageTranslatableResourceSections('shop');
  const named = SHOP_GROUPS.reduce((all, group) => all.concat(group.ids), []);
  const ungrouped = sections.filter(section => !named.includes(section.key));

  return SHOP_GROUPS.map((group, index) => ({
    ...group,
    sections: group.ids
      .map(id => sections.find(section => section.key === id))
      .filter(Boolean)
      .concat(index === 0 ? ungrouped : []),
  }));
};

// sections with a page of their own, so a route and a navigation entry
export const getRoutedTranslatableResourceSections = () =>
  TRANSLATABLE_RESOURCE_SECTIONS.filter(section => section.menu);

export const getTranslatableResourceSections = (menu) =>
  TRANSLATABLE_RESOURCE_SECTIONS.filter(section => section.menu === menu);

// sections rendered as a subsection of an existing page
export const getPageTranslatableResourceSections = (page) =>
  TRANSLATABLE_RESOURCE_SECTIONS.filter(section => section.page === page);
